<script>
    import { isMobile } from "@/utils";
    import LookbookTooltip from "@xd/components/LookbookTooltip";

    export default {
        name: "LookbookDot",
        components: { LookbookTooltip },
        props: {
            product: Object,
            offerId: String,
            isAnimated: Boolean,
            tooltipPosition: {
                type: String,
                default: "left-top",
            },
        },
        data() {
            return {
                isVisibleTooltip: false,
            };
        },
        mounted() {
            if (isMobile(991)) {
                window.addEventListener("scroll", this.closeTooltip);
            }
        },
        beforeDestroy() {
            window.removeEventListener("scroll", this.closeTooltip);
        },
        methods: {
            openTooltip() {
                this.isVisibleTooltip = true;
            },
            closeTooltip() {
                this.isVisibleTooltip = false;
            },
        },
    };
</script>

<template>
    <div
        class="lookbook-dot"
        :class="{ 'is-active': isVisibleTooltip, 'is-animated': isAnimated }"
        @click.self="openTooltip"
        @mouseenter="openTooltip"
        @mouseleave="closeTooltip"
    >
        <div class="lookbook-dot__pointer"></div>
        <div
            class="lookbook-dot__tooltip"
            :class="[`is-${tooltipPosition}`, { 'is-visible': isVisibleTooltip }]"
        >
            <lookbook-tooltip
                v-if="product"
                :product="product"
                :offer-id="offerId"
                @on-close="closeTooltip"
            ></lookbook-tooltip>
        </div>
    </div>
</template>

<style lang="scss">
    .lookbook-dot {
        position: absolute;
        @include box(28px);
        opacity: 1;
        pointer-events: auto;
        &.is-animated {
            pointer-events: none;
            &::before,
            &::after {
                opacity: 0;
            }
        }
        &.is-active {
            &::before {
                transform: scale(1.35);
            }
            &::after {
                animation: dotRipple 1.25s ease-in infinite;
            }
        }
        &::before,
        &::after {
            content: "";
            position: absolute;
            z-index: 11;
            border-radius: 50%;
        }
        &::before {
            top: 8px;
            left: 8px;
            @include box(12px);
            background-color: #fff;
            transition: transform 0.2s ease-in, opacity 0.2s ease-in 1.2s;
        }
        &::after {
            top: 0;
            left: 0;
            @include box(28px);
            background-color: rgba(#fff, 0.4);
            transition: opacity 0.2s ease-in 1.2s;
        }
        @include bp($bp-desktop-sm) {
            &.is-active {
                &::after {
                    animation: none;
                }
            }
        }
    }

    .lookbook-dot__pointer {
        position: absolute;
        z-index: 1;
        top: -10px;
        left: -10px;
        @include box(48px);
        @include bp($bp-desktop-sm) {
            display: none;
        }
    }

    .lookbook-dot__tooltip {
        position: fixed;
        z-index: 12;
        left: 8px;
        right: 8px;
        bottom: 80px;
        transition: visibility 0.3s ease-in, opacity 0.3s ease-in;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        &.is-visible {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
        }
        @include bp($bp-desktop-sm) {
            position: absolute;
            left: auto;
            right: auto;
            bottom: auto;
            width: 300px;
            transition-duration: 0.2s;
            &::before {
                content: "";
                @include center-absolute(-25px);
                z-index: -1;
            }
            &.is-left-top {
                bottom: 28px;
                right: 28px;
            }
            &.is-left-center {
                top: 50%;
                right: 40px;
                transform: translateY(-50%);
            }
            &.is-left-bottom {
                top: 100%;
                right: 28px;
            }
            &.is-center-top {
                bottom: 40px;
                left: 50%;
                transform: translateX(-50%);
            }
            &.is-center-bottom {
                top: calc(100% + 10px);
                left: 50%;
                transform: translateX(-50%);
            }
            &.is-right-top {
                bottom: 28px;
                left: 28px;
            }
            &.is-right-center {
                top: 50%;
                left: 40px;
                transform: translateY(-50%);
            }
            &.is-right-bottom {
                top: 100%;
                left: 28px;
            }
        }
    }

    @keyframes dotRipple {
        0% {
            opacity: 1;
            transform: scale(0.33);
        }
        80% {
            transform: scale(1);
        }
        100% {
            opacity: 0;
        }
    }
</style>
